import { motion } from "framer-motion";
import { Palette, Ruler, Shirt } from "lucide-react";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, Lines, MoreDetails, Panel, RoomNumber } from "@/components/mall/BillboardKit";

const INK = "#0B0B0B";
const APPAREL_IMG = "https://images.unsplash.com/photo-1716541424893-734612ddcabb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmb2xkZWQlMjB0LXNoaXJ0cyUyMHN0YWNrfGVufDB8fHx8MTc4Nzk2NDQ3NXww&ixlib=rb-4.1.0&q=85&w=1600&h=760&fit=crop";

const START = [
    ["A white shirt with one print location", "$15"],
    ["A colored shirt with a DTF print", "$15"],
    ["Each extra print location", "+$5"],
];

const UPCHARGE = [
    ["Sueded shirts", "+$5"],
    ["Sweatshirts", "+$5"],
    ["Hoodies", "+$10"],
    ["Zip-up hoodies", "+$15"],
    ["Tank tops and youth sizes", "$15 · no extra charge"],
];

const FREE = [
    "You can change the color of your design as many times as you want for free.",
    "We resize your design for free, from a baby onesie to a 5XL.",
    "We print on t-shirts, tank tops, sweatshirts, hoodies and more.",
    "White shirts print the same day. Colored shirts take a few days longer.",
];

/** Room 02 — the whole apparel wall: colored garments, embroidery and screen printing. */
export default function Apparel() {
    return (
        <BillboardFrame testid="frame-apparel" room="apparel" note="the apparel wall">
        <section className="bg-white" data-testid="mall-room-apparel">
            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12">
                <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
                    <div className="lg:col-span-6">
                        <RoomNumber n="02" room="Standard · Apparel" accent={INK} className="mt-6" />
                        <h2 className="mt-4 font-display text-4xl font-extrabold leading-[0.94] tracking-tighter text-zinc-900 sm:text-5xl" data-testid="apparel-headline">
                            WE PRINT COLORED SHIRTS, HOODIES, EMBROIDERY AND SCREEN PRINTS TOO.{" "}
                            <span className="text-zinc-400">THEY TAKE A FEW DAYS LONGER.</span>
                        </h2>
                        <p className="mt-5 max-w-xl text-base text-zinc-600 md:text-lg" data-testid="apparel-body">
                            This is everything we print that is not a same-day white shirt. Colored shirts and
                            hoodies are printed with DTF transfers. Embroidery is priced by the number of stitches.
                            Screen printing is priced by the number of ink colors. Every product shows how long it
                            takes before you order.
                        </p>
                        <div className="mt-8">
                            <ShopButton room="apparel" tone="ink" testid="room-apparel-cta"
                                title="Which shop's apparel do you want to see?" path={(s) => `/shirts?store=${s}`}>
                                <Shirt size={17} /> Shop Standard apparel
                            </ShopButton>
                        </div>
                    </div>

                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="lg:col-span-6">
                        <div className="dark-block relative overflow-hidden rounded-[28px]">
                            <img src={APPAREL_IMG} alt="White, grey and black blanks ready to print" data-testid="apparel-photo"
                                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-80" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                            <p className="absolute inset-x-0 bottom-0 p-5 text-sm text-white" data-testid="apparel-caption">
                                We order colored blanks for your job, so they take a few days longer than white shirts.
                            </p>
                        </div>
                    </motion.div>
                </div>

                <MoreDetails testid="apparel-details" className="mt-9" label="What it costs, what costs extra and what is free">
                <div className="grid gap-5 md:grid-cols-3">
                    <Panel icon={Ruler} title="What it costs" accent={INK} testid="apparel-start">
                        <Lines rows={START} accent={INK} />
                    </Panel>
                    <Panel icon={Shirt} title="What costs extra" accent={INK} testid="apparel-upcharge">
                        <Lines rows={UPCHARGE} accent={INK} />
                    </Panel>
                    <Panel icon={Palette} title="What is always free" accent={INK} testid="apparel-free">
                        <Bullets items={FREE} accent={INK} />
                    </Panel>
                </div>
                </MoreDetails>
            </div>
        </section>
        </BillboardFrame>
    );
}
